"use client";
import { useEffect, useRef, useState } from "react";

export interface InfiniteScrollDetectorProps {
    onLoadMore?: () => void | Promise<void>;
    loadingMore: boolean;
    hasMore: boolean;
    threshold: number;
    loadingContent?: React.ReactNode;
    noMoreContent?: React.ReactNode;
}

/**
 * 无限滚动检测器组件
 * 当用户滚动到底部时自动触发加载更多数据
 */
export const InfiniteScrollDetector = (props: InfiniteScrollDetectorProps) => {
    const { onLoadMore, loadingMore, hasMore, threshold, loadingContent, noMoreContent } = props;
    const detectorRef = useRef<HTMLDivElement>(null);
    const [isIntersecting, setIsIntersecting] = useState(false);

    // 创建 IntersectionObserver 来检测是否到达底部
    useEffect(() => {
        if (!onLoadMore || typeof IntersectionObserver === 'undefined') {
            return;
        }

        const detector = detectorRef.current;
        if (!detector) { return; }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                setIsIntersecting(entry.isIntersecting);
            },
            {
                threshold: 0.1,
                rootMargin: `${threshold}px 0px 0px 0px`, // 提前触发加载
            },
        );

        observer.observe(detector);

        return () => {
            observer.disconnect();
        };
    }, [onLoadMore, threshold]);

    // 当检测器进入可视区域且不在加载中且还有更多数据时，触发加载
    useEffect(() => {
        if (isIntersecting && !loadingMore && hasMore && onLoadMore) {
            try {
                const result = onLoadMore();
                // 如果返回 Promise，不需要特殊处理，由外部管理 loadingMore 状态
                if (result instanceof Promise) {
                    result.catch(() => {
                        // Error loading more data - handled silently
                    });
                }
            } catch (error) {
                // Error executing onLoadMore - handled silently
            }
        }
    }, [isIntersecting, loadingMore, hasMore, onLoadMore]);

    // 如果不启用无限滚动，不渲染任何内容
    if (!onLoadMore) {
        return null;
    }

    return (
        <div
            ref={detectorRef}
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
            }}
        >
            {loadingMore && loadingContent}
            {!loadingMore && !hasMore && noMoreContent}
            {/* 用于paddingBottom */}
            <div style={{ height: '16px', width: '100%' }} />
        </div>
    );
};
